import { useEffect, useSyncExternalStore } from 'react';

import {
  readSessionUser,
  rereadSessionUser,
  resetSessionUserReads,
} from '@personal-trainer-app/shared';

/*
 * Keeps the store's session user in step with the server's.
 *
 * Once per page load, a protected route asks the store to read the server's
 * session (`fetchUser`, the one path that does).
 *
 * - Signed out in the persisted store, it may be a session the API made during
 *   a redirect (social sign-in PRD, D4): a web social sign-in lands on
 *   /dashboard signed in on the server but signed out in the store. So the
 *   guard waits for the answer. A session found signs the store in; none says
 *   nothing, and the guard carries on to /login.
 * - Signed in, the persisted user shows at once and the read runs behind it,
 *   so a change made elsewhere (another browser, the app) replaces it. A
 *   session that has gone signs the store out, quietly.
 *
 * Coming back to the tab reads again, at most every 30 seconds, so a change
 * made elsewhere while the tab waited shows too. The apps read again the same
 * way (`rereadSessionUser`).
 */

let started = false;
let settled = false;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isSettled(): boolean {
  return settled;
}

function startPickup(): void {
  if (started) return;
  started = true;

  void readSessionUser().finally(() => {
    settled = true;
    listeners.forEach((listener) => listener());
  });
}

/** Back on the tab, signed in: read again, unless it was read just now. */
function readOnReturn(): void {
  if (document.visibilityState !== 'visible') return;
  void rereadSessionUser();
}

/** Forgets this page load's reads, as a new page load would. For tests. */
export function resetSessionPickup(): void {
  started = false;
  settled = false;
  resetSessionUserReads();
}

export interface UseSessionPickupReturn {
  /** Whether the server is being asked. The guard waits while it is. */
  isChecking: boolean;
}

/**
 * Reads the server's session once per page load, and again on coming back to
 * the tab. `needed` is the guard's "signed out in the store" state: only then
 * does it wait for the answer.
 */
export function useSessionPickup(needed: boolean): UseSessionPickupReturn {
  const done = useSyncExternalStore(subscribe, isSettled);

  useEffect(() => {
    startPickup();
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', readOnReturn);
    window.addEventListener('focus', readOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', readOnReturn);
      window.removeEventListener('focus', readOnReturn);
    };
  }, []);

  return { isChecking: needed && !done };
}
